/**
 * Serialization between the arrangement React state and the /selections API
 * shape.
 *
 * after-api's PUT /arrangement/selections accepts a snake_case allowlist
 * (unknown keys are ignored server-side):
 *   { selected_package_variant_id, included_urn, additional_urns,
 *     selected_keepsakes, service_answers, add_on_selections }
 *
 * This module is the only spot in the app that translates between the
 * camelCase React state and that snake_case API shape. Everything else in the
 * arrangement flow stays camelCase; do NOT introduce a generic camel↔snake
 * mapper — the API is snake_case and ArrangementState is camelCase, and these
 * two functions are the seam.
 *
 * Engraving (includedUrnEngraving + per-urn engraving) and fulfillment
 * (delivery address) ARE persisted — the order + SOGS builders rely on them.
 * The engraving value-object is passed through verbatim (its internals stay
 * camelCase, shared with the Shopify formatter); only the API field names are
 * snake_case. Remaining non-selections fields (contactInfo, isDeceased,
 * quoteEmails, paymentMethodId) are intentionally not sent — they're unused
 * server-side (contactInfo) or handled elsewhere (paymentMethodId).
 */

import type {
  ArrangementState,
  DispositionMethod,
  EngravingOptions,
  ServiceAnswer,
} from "./types";

interface ApiShippingAddress {
  recipient_name?: string;
  street?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface ApiFulfillment {
  disposition_method?: DispositionMethod;
  shipping_address?: ApiShippingAddress;
  special_requests?: string;
}

interface SelectionsBody {
  selected_package_variant_id: string | null;
  included_urn: { id: string; variant_id: string } | null;
  included_urn_engraving: EngravingOptions | null;
  additional_urns: {
    id: string;
    variant_id: string;
    quantity: number;
    engraving?: EngravingOptions;
  }[];
  selected_keepsakes: {
    id: string;
    variant_id: string;
    quantity: number;
    fillable?: boolean;
  }[];
  service_answers: {
    question_id: string;
    value: string | number | null;
  }[];
  add_on_selections: { service_id: string; quantity: number }[];
  fulfillment: ApiFulfillment;
}

function serializeFulfillment(
  fulfillment: ArrangementState["fulfillment"],
): ApiFulfillment {
  const addr = fulfillment.shippingAddress;
  return {
    disposition_method: fulfillment.dispositionMethod,
    shipping_address: {
      recipient_name: addr.recipientName,
      street: addr.street,
      address_line_2: addr.addressLine2,
      city: addr.city,
      state: addr.state,
      zip: addr.zip,
    },
    special_requests: fulfillment.specialRequests,
  };
}

function serializeServiceAnswer(
  answer: ServiceAnswer,
): SelectionsBody["service_answers"][number] {
  if (answer.selectedOptionId !== undefined) {
    return { question_id: answer.questionId, value: answer.selectedOptionId };
  }
  if (answer.quantity !== undefined) {
    return { question_id: answer.questionId, value: answer.quantity };
  }
  return { question_id: answer.questionId, value: null };
}

export function serializeArrangementSelections(
  state: ArrangementState,
): SelectionsBody {
  const includedUrn =
    state.includedUrnId && state.includedUrnVariantId
      ? { id: state.includedUrnId, variant_id: state.includedUrnVariantId }
      : null;

  return {
    selected_package_variant_id: state.selectedPackageId,
    included_urn: includedUrn,
    included_urn_engraving: state.includedUrnEngraving ?? null,
    additional_urns: state.additionalUrns.map((sel) => ({
      id: sel.urnId,
      variant_id: sel.variantId,
      quantity: sel.quantity,
      ...(sel.engraving ? { engraving: sel.engraving } : {}),
    })),
    selected_keepsakes: state.selectedKeepsakes.map((sel) => ({
      id: sel.productId,
      variant_id: sel.variantId,
      quantity: sel.quantity,
      ...(typeof sel.fillable === "boolean" ? { fillable: sel.fillable } : {}),
    })),
    service_answers: state.serviceAnswers.map(serializeServiceAnswer),
    add_on_selections: state.addOnSelections.map((sel) => ({
      service_id: sel.serviceId,
      quantity: sel.quantity,
    })),
    fulfillment: serializeFulfillment(state.fulfillment),
  };
}

interface ApiArrangement {
  selected_package_variant_id?: string | null;
  included_urn?: { id: string; variant_id: string } | null;
  included_urn_engraving?: EngravingOptions | null;
  additional_urns?: {
    id: string;
    variant_id: string;
    quantity: number;
    engraving?: EngravingOptions | null;
  }[];
  selected_keepsakes?: {
    id: string;
    variant_id: string;
    quantity: number;
    fillable?: boolean;
  }[];
  service_answers?: {
    question_id: string;
    value: string | number | null;
  }[];
  add_on_selections?: { service_id: string; quantity: number }[];
  fulfillment?: ApiFulfillment;
}

function deserializeFulfillment(
  fulfillment: ApiFulfillment,
): ArrangementState["fulfillment"] {
  const addr = fulfillment.shipping_address ?? {};
  return {
    dispositionMethod: fulfillment.disposition_method ?? "",
    shippingAddress: {
      recipientName: addr.recipient_name ?? "",
      street: addr.street ?? "",
      addressLine2: addr.address_line_2 ?? "",
      city: addr.city ?? "",
      state: addr.state ?? "",
      zip: addr.zip ?? "",
    },
    specialRequests: fulfillment.special_requests ?? "",
    noUnitConfirmed: false,
  };
}

function deserializeServiceAnswer(
  entry: NonNullable<ApiArrangement["service_answers"]>[number],
): ServiceAnswer {
  if (typeof entry.value === "string") {
    return { questionId: entry.question_id, selectedOptionId: entry.value };
  }
  if (typeof entry.value === "number") {
    return { questionId: entry.question_id, quantity: entry.value };
  }
  return { questionId: entry.question_id };
}

export function deserializeArrangementSelections(
  arrangement: ApiArrangement | null | undefined,
): Partial<ArrangementState> {
  if (!arrangement) return {};
  const patch: Partial<ArrangementState> = {};

  if (arrangement.selected_package_variant_id !== undefined) {
    patch.selectedPackageId = arrangement.selected_package_variant_id;
  }
  if (arrangement.included_urn !== undefined) {
    patch.includedUrnId = arrangement.included_urn?.id ?? null;
    patch.includedUrnVariantId = arrangement.included_urn?.variant_id ?? null;
  }
  if (arrangement.included_urn_engraving !== undefined) {
    patch.includedUrnEngraving = arrangement.included_urn_engraving;
  }
  if (arrangement.additional_urns) {
    patch.additionalUrns = arrangement.additional_urns.map((u) => ({
      urnId: u.id,
      variantId: u.variant_id,
      quantity: u.quantity,
      ...(u.engraving ? { engraving: u.engraving } : {}),
    }));
  }
  if (arrangement.fulfillment) {
    patch.fulfillment = deserializeFulfillment(arrangement.fulfillment);
  }
  if (arrangement.selected_keepsakes) {
    patch.selectedKeepsakes = arrangement.selected_keepsakes.map((k) => ({
      productId: k.id,
      variantId: k.variant_id,
      quantity: k.quantity,
      ...(typeof k.fillable === "boolean" ? { fillable: k.fillable } : {}),
    }));
  }
  if (arrangement.service_answers) {
    patch.serviceAnswers = arrangement.service_answers.map(
      deserializeServiceAnswer,
    );
  }
  if (arrangement.add_on_selections) {
    patch.addOnSelections = arrangement.add_on_selections.map((a) => ({
      serviceId: a.service_id,
      quantity: a.quantity,
    }));
  }
  return patch;
}
